<template>
  <div class="login-page" @mousemove="onMouseMove">

    <!-- ── Decorative right panel (shared brand look with login) ── -->
    <div class="login-deco" ref="decoPanel">
      <div class="ld-blob ld-blob-1"></div>
      <div class="ld-blob ld-blob-2"></div>
      <div class="ld-blob ld-blob-3"></div>

      <span class="lm lm-1">∑</span>
      <span class="lm lm-2">π</span>
      <span class="lm lm-3">∞</span>
      <span class="lm lm-4">√</span>
      <span class="lm lm-5">∫</span>
      <span class="lm lm-6">±</span>
      <span class="lm lm-7">θ</span>
      <span class="lm lm-8">Δ</span>
      <span class="lm lm-9">λ</span>
      <span class="lm lm-10">≠</span>
      <span class="lm lm-11">φ</span>
      <span class="lm lm-12">∂</span>

      <div class="ld-avatar-wrap">
        <div class="ld-avatar-orbit ld-orbit-1"></div>
        <div class="ld-avatar-orbit ld-orbit-2"></div>
        <div class="ld-avatar-ring">
          <img src="@/assets/images/favicon.png" alt="أ/طارق عبدالغني عبدالباري" />
        </div>
      </div>

      <div class="ld-text">
        <h1 class="ld-heading">ابدأ رحلتك في التفوق</h1>
        <p class="ld-sub">مع أ/ طارق عبدالغني عبدالباري — مدرس الرياضيات</p>
        <div class="ld-badges">
          <span class="ld-badge">🏆 2000+ طالب ناجح</span>
          <span class="ld-badge">📚 350+ درس</span>
          <span class="ld-badge">⭐ 95% نجاح</span>
        </div>
      </div>
    </div>

    <!-- ── Form left panel ── -->
    <div class="login-form-panel">
      <div class="login-card">

        <div class="lc-logo">
          <img src="@/assets/images/favicon.png" alt="logo" />
        </div>

        <h2 class="lc-title">إنشاء حساب</h2>
        <p class="lc-subtitle">أدخل بياناتك لإنشاء حساب جديد</p>

        <form @submit.prevent="submit" class="lc-form" novalidate>

          <!-- (7) Academic year — loaded dynamically; chosen first so education
               options (عام/أزهر) can load for the selected year. -->
          <div class="lf-group">
            <label class="lf-label">السنة الدراسية <span class="rg-req">*</span></label>
            <div class="lf-input-wrap">
              <span class="lf-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 19.5A2.5 2.5 0 016.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z"/></svg>
              </span>
              <select v-model="form.academic_year_id" class="lf-input rg-select" @change="onYearChange" :disabled="loadingYears">
                <option :value="null" disabled>
                  {{ loadingYears ? 'جارٍ تحميل السنوات الدراسية...' : 'اختر السنة الدراسية' }}
                </option>
                <option v-for="y in academicYears" :key="y.id" :value="y.id">{{ y.name }}</option>
              </select>
            </div>
            <span class="rg-error" v-if="err('academic_year_id')">{{ err('academic_year_id') }}</span>
            <!-- Loading / empty / error feedback (never a silently-empty dropdown). -->
            <p class="rg-hint rg-hint--muted" v-if="!loadingYears && !academicYears.length">
              <template v-if="yearsError">تعذّر تحميل السنوات الدراسية.</template>
              <template v-else>لا توجد سنوات دراسية متاحة للتسجيل حالياً.</template>
              <button type="button" class="rg-retry" @click="fetchAcademicYears">إعادة المحاولة</button>
            </p>
          </div>

          <!-- (1) Name -->
          <div class="lf-group">
            <label class="lf-label">الاسم <span class="rg-req">*</span></label>
            <div class="lf-input-wrap">
              <span class="lf-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
              </span>
              <input type="text" v-model="form.student_name" class="lf-input" placeholder="الاسم ثلاثي عربي" />
            </div>
            <span class="rg-error" v-if="err('student_name')">{{ err('student_name') }}</span>
          </div>

          <!-- (6) Gender -->
          <div class="lf-group">
            <label class="lf-label">النوع <span class="rg-req">*</span></label>
            <div class="rg-pill-group">
              <button type="button" v-for="g in genders" :key="g.id"
                class="rg-pill" :class="{ 'rg-pill--active': form.gender === g.id }"
                @click="form.gender = g.id">
                {{ g.name }}
              </button>
            </div>
            <span class="rg-error" v-if="err('gender')">{{ err('gender') }}</span>
          </div>

          <!-- (2) Student phone -->
          <div class="lf-group">
            <label class="lf-label">رقم الهاتف <span class="rg-req">*</span></label>
            <div class="lf-input-wrap">
              <span class="lf-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.13.96.36 1.9.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.9.34 1.85.57 2.81.7A2 2 0 0122 16.92z"/></svg>
              </span>
              <input type="tel" v-model="form.student_phone" class="lf-input" placeholder="01XXXXXXXXX" dir="ltr" inputmode="tel" v-phone />
            </div>
            <span class="rg-error" v-if="err('student_phone')">{{ err('student_phone') }}</span>
          </div>

          <!-- (3) Parent phone -->
          <div class="lf-group">
            <label class="lf-label">رقم ولي الأمر</label>
            <div class="lf-input-wrap">
              <span class="lf-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/></svg>
              </span>
              <input type="tel" v-model="form.parent_phone" class="lf-input" placeholder="01XXXXXXXXX" dir="ltr" inputmode="tel" v-phone />
            </div>
            <span class="rg-error" v-if="err('parent_phone')">{{ err('parent_phone') }}</span>
          </div>

          <!-- (4) WhatsApp number -->
          <div class="lf-group">
            <label class="lf-label">رقم الواتساب</label>
            <div class="lf-input-wrap">
              <span class="lf-icon">
                <svg viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/></svg>
              </span>
              <input type="tel" v-model="form.whatsapp_number" class="lf-input" placeholder="01XXXXXXXXX" dir="ltr" inputmode="tel" v-phone />
            </div>
            <span class="rg-error" v-if="err('whatsapp_number')">{{ err('whatsapp_number') }}</span>
          </div>

          <!-- (5) Education type (عام / أزهر) — options come from the selected
               year's registration settings (never hardcoded). -->
          <div class="lf-group" v-if="educationOptions.length">
            <label class="lf-label">نوع التعليم <span class="rg-req" v-if="educationRequired">*</span></label>
            <div class="rg-pill-group">
              <button type="button" v-for="t in educationOptions" :key="t.id"
                class="rg-pill"
                :class="{ 'rg-pill--active': form.education_type === t.id, 'rg-pill--disabled': t.available === false }"
                @click="selectEducation(t)">
                {{ t.name }}
                <span v-if="t.available === false" class="rg-pill-badge">غير متاح حالياً</span>
              </button>
            </div>
            <span class="rg-error" v-if="err('education_type')">{{ err('education_type') }}</span>
          </div>
          <p class="rg-hint" v-else-if="form.academic_year_id && !loadingSettings">
            اختر السنة الدراسية لعرض نوع التعليم المتاح.
          </p>

          <button type="submit" class="lc-submit" :class="{ 'lc-submit--loading': submitting }" :disabled="submitting">
            <span v-if="!submitting">✏️ إنشاء الحساب</span>
            <span v-else class="lc-spinner"></span>
          </button>

        </form>

        <!-- Back to login -->
        <p class="lc-foot">
          لديك حساب بالفعل؟
          <router-link to="/login" class="lc-link">تسجيل الدخول</router-link>
        </p>

        <p class="lc-legal">
          بإنشاء الحساب فإنك توافق على
          <router-link to="/terms-of-service" class="lc-link">شروط الاستخدام</router-link>
          و
          <router-link to="/privacy-policy" class="lc-link">سياسة الخصوصية</router-link>
        </p>
      </div>
    </div>

  </div>
</template>

<script>
import axios from 'axios';
import Swal from 'sweetalert2';

// Arabic-Indic → Western digits (backend also normalises, this keeps the UI tidy).
function toEnglishDigits(value) {
  if (value == null) return value;
  return String(value)
    .replace(/[٠-٩]/g, (d) => String(d.charCodeAt(0) - 0x0660))
    .replace(/[۰-۹]/g, (d) => String(d.charCodeAt(0) - 0x06F0));
}

export default {
  name: 'register-account',
  data() {
    return {
      academicYears: [],
      loadingYears: false,
      yearsError: false,
      loadingSettings: false,
      educationOptions: [],   // [{ id, name, available }] from the selected year's settings
      educationRequired: false,
      submitting: false,
      // Genders match the backend contract (gender is a numeric id: 1=ولد, 2=بنت).
      genders: [
        { name: 'ولد', id: 1 },
        { name: 'بنت', id: 2 },
      ],
      form: {
        student_name: '',
        student_phone: '',
        parent_phone: '',
        whatsapp_number: '',
        gender: null,
        education_type: null,
        academic_year_id: null,
      },
      // Server-side (Laravel 422) validation errors keyed by field name.
      serverErrors: {},
    };
  },
  mounted() {
    this.fetchAcademicYears();
  },
  methods: {
    onMouseMove(e) {
      const panel = this.$refs.decoPanel;
      if (!panel) return;
      const rect = panel.getBoundingClientRect();
      const dx = (e.clientX - (rect.left + rect.width / 2)) / rect.width;
      const dy = (e.clientY - (rect.top + rect.height / 2)) / rect.height;
      panel.style.setProperty('--mx', dx.toFixed(3));
      panel.style.setProperty('--my', dy.toFixed(3));
    },
    onlyNumbers(e) {
      const ok = /[0-9٠-٩۰-۹]/.test(e.key);
      if (!ok) e.preventDefault();
    },
    /** First backend error message for a field (if any). */
    err(field) {
      const e = this.serverErrors[field];
      return Array.isArray(e) ? e[0] : (e || '');
    },
    async fetchAcademicYears() {
      // Registration lists years by ACTIVE/ENABLED status ONLY — booking
      // (is_reservation_open) has ZERO effect here. Reuses the existing public
      // endpoint the admin dashboard also uses:
      //   GET /academic-years/active → { status, data: [...years] }
      //   backend scope AcademicYear::active() = where('is_active', true).
      // (NOT /reservation-available, which additionally requires is_reservation_open.)
      // The active-year rule lives in the backend scope; it is NOT duplicated here.
      this.loadingYears = true;
      this.yearsError = false;
      try {
        const { data } = await axios.get('/academic-years/active');
        this.academicYears = Array.isArray(data?.data) ? data.data : [];
        if (!this.academicYears.length) {
          // eslint-disable-next-line no-console
          console.warn('[register] /academic-years/active returned no active academic years.');
        }
      } catch (e) {
        this.yearsError = true;
        this.academicYears = [];
        // eslint-disable-next-line no-console
        console.warn('[register] failed to load academic years', e);
      } finally {
        this.loadingYears = false;
      }
    },
    async onYearChange() {
      // Reset the dependent education choice + errors, then load the year's
      // registration settings so we can render its education-type options.
      this.form.education_type = null;
      this.educationOptions = [];
      this.educationRequired = false;
      delete this.serverErrors.education_type;
      delete this.serverErrors.academic_year_id;
      if (!this.form.academic_year_id) return;

      this.loadingSettings = true;
      try {
        const { data } = await axios.get(`/academic-years/${this.form.academic_year_id}/registration-settings`);
        const et = data?.form_settings?.education_type;
        if (et && et.visible !== false && Array.isArray(et.options)) {
          this.educationOptions = et.options;
          this.educationRequired = !!et.required;
        }
      } catch (e) {
        // Non-fatal — the backend re-validates education_type on submit anyway.
        this.educationOptions = [];
      } finally {
        this.loadingSettings = false;
      }
    },
    selectEducation(t) {
      if (t.available === false) {
        Swal.fire({ icon: 'warning', title: 'هذا النظام غير متاح حالياً.', toast: true, position: 'top', timer: 2500, showConfirmButton: false });
        return;
      }
      this.form.education_type = t.id;
      delete this.serverErrors.education_type;
    },
    validateClient() {
      this.serverErrors = {};
      if (!this.form.academic_year_id) this.serverErrors.academic_year_id = ['برجاء اختيار السنة الدراسية.'];
      if (!this.form.student_name || !this.form.student_name.trim()) this.serverErrors.student_name = ['برجاء إدخال اسم الطالب.'];
      if (!this.form.student_phone) this.serverErrors.student_phone = ['برجاء إدخال رقم الهاتف.'];
      if (!this.form.gender) this.serverErrors.gender = ['برجاء اختيار النوع.'];
      if (this.educationRequired && !this.form.education_type) this.serverErrors.education_type = ['برجاء اختيار نوع التعليم.'];
      return Object.keys(this.serverErrors).length === 0;
    },
    async submit() {
      if (!this.validateClient()) {
        const first = Object.values(this.serverErrors)[0]?.[0];
        Swal.fire({ position: 'center', icon: 'warning', title: first || 'برجاء التحقق من البيانات' });
        return;
      }

      this.submitting = true;
      try {
        // Build the payload with the EXACT field names the backend expects.
        // IMPORTANT: study_mode is intentionally NOT sent — the backend forces
        // study_mode='online' for public registration.
        const fd = new FormData();
        fd.append('student_name', this.form.student_name.trim());
        fd.append('student_phone', toEnglishDigits(this.form.student_phone));
        if (this.form.parent_phone) fd.append('parent_phone', toEnglishDigits(this.form.parent_phone));
        fd.append('gender', this.form.gender);
        fd.append('academic_year_id', this.form.academic_year_id);
        if (this.form.whatsapp_number) fd.append('whatsapp_number', toEnglishDigits(this.form.whatsapp_number));
        if (this.form.education_type) fd.append('education_type', this.form.education_type);

        const { data } = await axios.post('/register', fd);

        // Success modal shows ONLY the account-created confirmation + the real
        // reservation number returned by the API and the login credentials hint.
        // The backend `after_submit_message` (accepted/rejected / "visit again")
        // is intentionally NOT displayed here.
        const reservationNumber = data?.data?.reservation_number;
        const reservationBlock = reservationNumber
          ? `
            <div style="margin-top:0.25rem;color:#6B7280;font-size:0.9rem;font-weight:700;">رقم الحجز الخاص بك</div>
            <div style="font-size:2.4rem;font-weight:900;color:#5B5FEF;line-height:1.15;margin:0.15rem 0 1rem;">${reservationNumber}</div>
            <div style="background:#F5F7FB;border:1px solid #E5E7EB;border-radius:14px;padding:0.9rem 1rem;text-align:right;">
              <div style="font-size:0.9rem;font-weight:800;color:#1A1A1A;margin-bottom:0.55rem;">بيانات تسجيل الدخول</div>
              <div style="font-size:0.9rem;color:#374151;margin-bottom:0.3rem;">رقم الحجز: <strong style="color:#5B5FEF;">${reservationNumber}</strong></div>
              <div style="font-size:0.9rem;color:#374151;">كلمة المرور: <strong>رقم الهاتف الخاص بك</strong></div>
            </div>
            <p style="font-size:0.78rem;color:#9CA3AF;margin-top:0.75rem;line-height:1.7;">
              يمكنك استخدام رقم الحجز كاسم مستخدم، ورقم الهاتف الذي سجّلت به ككلمة مرور لتسجيل الدخول.
            </p>`
          : '';
        await Swal.fire({
          position: 'center',
          icon: 'success',
          title: 'تم إنشاء حسابك بنجاح',
          html: `<div style="direction:rtl;text-align:center;font-family:'Cairo',sans-serif;">${reservationBlock}</div>`,
          confirmButtonText: 'تسجيل الدخول',
        });
        this.$router.push('/login');
      } catch (error) {
        const res = error.response;
        if (res?.status === 422 && res.data?.errors) {
          // Surface Laravel field errors inline + a summary toast.
          this.serverErrors = res.data.errors;
          const first = Object.values(res.data.errors)[0]?.[0];
          Swal.fire({ position: 'center', icon: 'error', title: first || 'برجاء التحقق من البيانات' });
        } else {
          Swal.fire({ position: 'center', icon: 'error', title: res?.data?.message || 'حدث خطأ، برجاء المحاولة مرة أخرى' });
        }
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Cairo:wght@400;600;700;800;900&display=swap');

*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

/* ─── Page layout (shared with login) ─── */
.login-page {
  display: flex;
  flex-direction: row;
  min-height: 100vh;
  overflow: hidden;
  font-family: 'Cairo', sans-serif;
  direction: rtl;
}

/* Decorative panel */
.login-deco {
  flex: 1;
  position: relative;
  background: linear-gradient(135deg, #3730a3 0%, #5B5FEF 35%, #7C4DFF 65%, #a855f7 100%);
  overflow: hidden;
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  padding: 3rem 2.5rem; gap: 2rem; --mx: 0; --my: 0;
}
.ld-blob { position: absolute; border-radius: 50%; filter: blur(60px); opacity: 0.4; pointer-events: none; animation: ldBlobFloat 8s ease-in-out infinite; }
.ld-blob-1 { width: 420px; height: 420px; background: radial-gradient(circle, #00C2FF, transparent); top: -100px; right: -80px; animation-duration: 9s; }
.ld-blob-2 { width: 350px; height: 350px; background: radial-gradient(circle, #00D4A6, transparent); bottom: -60px; left: -60px; animation-duration: 11s; animation-delay: -3s; }
.ld-blob-3 { width: 250px; height: 250px; background: radial-gradient(circle, #f472b6, transparent); top: 50%; left: 50%; transform: translate(-50%, -50%); animation-duration: 7s; animation-delay: -5s; }
@keyframes ldBlobFloat { 0%,100% { transform: scale(1) translate(0,0); } 33% { transform: scale(1.08) translate(15px,-20px); } 66% { transform: scale(0.95) translate(-10px,15px); } }

.lm { position: absolute; font-size: 2rem; font-weight: 800; color: rgba(255,255,255,0.15); pointer-events: none; animation: ldFloat 6s ease-in-out infinite; user-select: none; }
.lm-1 { top: 8%; right: 12%; font-size: 2.5rem; animation-duration: 7s; }
.lm-2 { top: 15%; left: 18%; font-size: 3rem; animation-duration: 9s; animation-delay: -1s; }
.lm-3 { top: 25%; right: 5%; font-size: 1.8rem; animation-duration: 6s; animation-delay: -2s; }
.lm-4 { top: 40%; left: 8%; font-size: 2.2rem; animation-duration: 8s; animation-delay: -3s; }
.lm-5 { top: 60%; right: 10%; font-size: 3.5rem; animation-duration: 10s; animation-delay: -1.5s; }
.lm-6 { top: 72%; left: 15%; font-size: 2rem; animation-duration: 7s; animation-delay: -4s; }
.lm-7 { top: 80%; right: 20%; font-size: 1.6rem; animation-duration: 6s; animation-delay: -2.5s; }
.lm-8 { top: 90%; left: 30%; font-size: 2.8rem; animation-duration: 9s; animation-delay: -0.5s; }
.lm-9 { top: 5%; left: 40%; font-size: 1.4rem; animation-duration: 8s; animation-delay: -3.5s; }
.lm-10 { top: 50%; right: 30%; font-size: 2rem; animation-duration: 11s; animation-delay: -2s; }
.lm-11 { top: 30%; left: 30%; font-size: 1.6rem; animation-duration: 7s; animation-delay: -4.5s; }
.lm-12 { top: 85%; right: 40%; font-size: 2.4rem; animation-duration: 9s; animation-delay: -1s; }
@keyframes ldFloat { 0%,100% { transform: translateY(0) rotate(0deg); opacity: 0.15; } 50% { transform: translateY(-22px) rotate(8deg); opacity: 0.28; } }

.ld-avatar-wrap { position: relative; width: 160px; height: 160px; flex-shrink: 0; z-index: 2; transform: translateX(calc(var(--mx) * -12px)) translateY(calc(var(--my) * -8px)); transition: transform 0.1s ease-out; }
.ld-avatar-orbit { position: absolute; inset: 0; border-radius: 50%; border: 2px solid rgba(255,255,255,0.25); animation: ldOrbit 8s linear infinite; }
.ld-orbit-1 { inset: -16px; border-color: rgba(255,255,255,0.15); animation-duration: 10s; }
.ld-orbit-2 { inset: -30px; border-color: rgba(0,194,255,0.2); animation-duration: 14s; animation-direction: reverse; }
@keyframes ldOrbit { to { transform: rotate(360deg); } }
.ld-avatar-ring { position: absolute; inset: 0; border-radius: 50%; background: linear-gradient(135deg, #fff 0%, rgba(255,255,255,0.8) 100%); padding: 6px; box-shadow: 0 20px 60px rgba(0,0,0,0.25), 0 0 0 4px rgba(255,255,255,0.3); overflow: hidden; display: flex; align-items: center; justify-content: center; }
.ld-avatar-ring img { width: 100%; height: 100%; object-fit: cover; border-radius: 50%; }

.ld-text { text-align: center; z-index: 2; transform: translateX(calc(var(--mx) * -6px)); transition: transform 0.1s ease-out; }
.ld-heading { font-size: clamp(1.8rem, 3vw, 2.6rem); font-weight: 900; color: #fff; line-height: 1.2; margin-bottom: 0.5rem; text-shadow: 0 4px 20px rgba(0,0,0,0.2); }
.ld-sub { font-size: 1rem; color: rgba(255,255,255,0.8); margin-bottom: 1.5rem; }
.ld-badges { display: flex; flex-wrap: wrap; justify-content: center; gap: 0.6rem; }
.ld-badge { background: rgba(255,255,255,0.15); backdrop-filter: blur(10px); border: 1px solid rgba(255,255,255,0.25); color: #fff; padding: 0.35rem 0.9rem; border-radius: 99px; font-size: 0.82rem; font-weight: 700; }

/* Form panel — scrollable (more fields than login) */
.login-form-panel { width: 480px; flex-shrink: 0; display: flex; align-items: center; justify-content: center; background: #F5F7FB; padding: 2rem 1.5rem; position: relative; max-height: 100vh; overflow-y: auto; }
.login-card { width: 100%; max-width: 400px; background: rgba(255,255,255,0.92); backdrop-filter: blur(20px); border-radius: 28px; padding: 2.25rem 2rem; box-shadow: 0 24px 80px rgba(91,95,239,0.12), 0 4px 16px rgba(0,0,0,0.06); border: 1px solid rgba(255,255,255,0.8); }

.lc-logo { width: 64px; height: 64px; border-radius: 18px; background: linear-gradient(135deg, #5B5FEF, #7C4DFF); padding: 10px; margin: 0 auto 1.1rem; box-shadow: 0 12px 30px rgba(91,95,239,0.35); overflow: hidden; }
.lc-logo img { width: 100%; height: 100%; object-fit: contain; }
.lc-title { font-size: 1.6rem; font-weight: 800; color: #1A1A1A; text-align: center; margin-bottom: 0.3rem; }
.lc-subtitle { font-size: 0.9rem; color: #6B7280; text-align: center; margin-bottom: 1.6rem; }

.lc-form { display: flex; flex-direction: column; gap: 1rem; }
.lf-group { position: relative; }
.lf-label { display: block; font-size: 0.85rem; font-weight: 700; color: #6B7280; margin-bottom: 0.4rem; }
.lf-input-wrap { position: relative; display: flex; align-items: center; }
.lf-icon { position: absolute; right: 14px; width: 20px; height: 20px; color: #9CA3AF; pointer-events: none; display: flex; align-items: center; justify-content: center; }
.lf-icon svg { width: 18px; height: 18px; }
.lf-input { width: 100%; padding: 0.8rem 2.75rem 0.8rem 1rem; border: 2px solid #E5E7EB; border-radius: 14px; font-family: 'Cairo', sans-serif; font-size: 0.95rem; color: #1A1A1A; background: #F9FAFB; outline: none; transition: border-color 0.25s, box-shadow 0.25s, background 0.25s; text-align: right; }
.lf-input:focus { border-color: #5B5FEF; background: #fff; box-shadow: 0 0 0 4px rgba(91,95,239,0.12); }
.lf-input::placeholder { color: #C4C9D4; }
.rg-select { appearance: none; -webkit-appearance: none; cursor: pointer; }

/* Pill groups (gender + education type) */
.rg-pill-group { display: flex; flex-wrap: wrap; gap: 0.5rem; }
.rg-pill { position: relative; flex: 1 1 auto; min-width: 90px; padding: 0.7rem 1rem; border: 2px solid #E5E7EB; border-radius: 14px; background: #F9FAFB; font-family: 'Cairo', sans-serif; font-size: 0.9rem; font-weight: 700; color: #4B5563; cursor: pointer; transition: all 0.2s; }
.rg-pill:hover { border-color: #C7D2FE; }
.rg-pill--active { border-color: #5B5FEF; background: #EEF0FF; color: #5B5FEF; box-shadow: 0 0 0 3px rgba(91,95,239,0.12); }
.rg-pill--disabled { opacity: 0.55; cursor: not-allowed; }
.rg-pill-badge { display: block; font-size: 0.65rem; font-weight: 600; color: #b91c1c; margin-top: 2px; }

.rg-req { color: #ef4444; }
.rg-error { display: block; color: #ef4444; font-size: 0.78rem; font-weight: 600; margin-top: 0.35rem; }
.rg-hint { font-size: 0.8rem; color: #9CA3AF; }
.rg-hint--muted { margin-top: 0.4rem; display: flex; align-items: center; flex-wrap: wrap; gap: 0.4rem; }
.rg-retry { background: none; border: none; color: #5B5FEF; font-family: 'Cairo', sans-serif; font-weight: 700; font-size: 0.8rem; cursor: pointer; padding: 0; text-decoration: underline; }
.rg-retry:hover { color: #7C4DFF; }

.lc-submit { width: 100%; padding: 0.95rem; border: none; border-radius: 14px; background: linear-gradient(135deg, #5B5FEF, #7C4DFF); color: #fff; font-family: 'Cairo', sans-serif; font-size: 1.05rem; font-weight: 800; cursor: pointer; box-shadow: 0 12px 30px rgba(91,95,239,0.35); transition: all 0.3s; display: flex; align-items: center; justify-content: center; margin-top: 0.5rem; min-height: 54px; }
.lc-submit:hover:not(:disabled) { transform: translateY(-3px); box-shadow: 0 20px 45px rgba(91,95,239,0.45); }
.lc-submit--loading { opacity: 0.85; cursor: not-allowed; }
.lc-spinner { width: 24px; height: 24px; border: 3px solid rgba(255,255,255,0.35); border-top-color: #fff; border-radius: 50%; animation: lcSpin 0.8s linear infinite; display: inline-block; }
@keyframes lcSpin { to { transform: rotate(360deg); } }

.lc-foot { text-align: center; font-size: 0.85rem; color: #9CA3AF; margin-top: 1.25rem; }
.lc-link { color: #5B5FEF; font-weight: 700; text-decoration: none; transition: color 0.2s; }
.lc-link:hover { color: #7C4DFF; }
.lc-legal { text-align: center; font-size: 0.78rem; color: #9CA3AF; line-height: 1.7; margin-top: 0.85rem; }

@media (max-width: 900px) {
  .login-page { flex-direction: column; }
  .login-deco { min-height: 240px; padding: 2rem 1.5rem; gap: 1rem; }
  .ld-avatar-wrap { width: 100px; height: 100px; }
  .ld-heading { font-size: 1.5rem; }
  .login-form-panel { width: 100%; padding: 2rem 1.25rem 3rem; max-height: none; }
  .login-card { max-width: 480px; }
  .lm-7, .lm-8, .lm-9, .lm-10, .lm-11, .lm-12 { display: none; }
}
@media (max-width: 480px) {
  .login-card { padding: 1.75rem 1.25rem; border-radius: 20px; }
  .lc-title { font-size: 1.35rem; }
}
</style>
